import {
    ApolloError,
    useMutation,
    useQuery,
    type DocumentNode,
} from '@apollo/client'
import { useTheme } from '@emotion/react'
import {
    Autocomplete,
    Box,
    Divider,
    FormControlLabel,
    Stack,
    Switch,
    TextField,
    Typography,
} from '@mui/material'
import {
    GetAddEditRosterOptionsQuery,
    GetCompaniesByNameQuery,
    GetCompanySpecificOptionsQuery,
    ProcessStatus,
    RosterProfileQuery,
    RosterProfileQueryVariables,
    RosterStatus,
    RosterType,
} from '__generated__/graphql'
import { useFormik } from 'formik'
import { FC, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { recruiterRouteConfig } from 'recruiter/routes'
import { rosterStatusOptions } from 'recruiter/types/RosterStatuses'
import DrawerCancelSave from 'shared/components/DrawerCancelSave'
import useToast from 'shared/components/Toast/useToast'
import { ApolloQueryRefetchFunction } from 'shared/types/graphql'
import { getFullName, getPxAsRem } from 'shared/utils'
import * as yup from 'yup'
import { RosterDrawerProps } from '.'
import CompaniesAutocomplete from '../../../shared/components/CompaniesAutocomplete/CompaniesAutocomplete'
import {
    CreateRoster,
    EditRoster,
    GetAddEditRosterOptions,
    GetCompanySpecificOptions,
    GetRosterData,
} from './AddOrEdit.gql'

import { CandidateFilterStateWithMetadata } from 'shared/utils/mappingFilterConverter'

interface Props extends RosterDrawerProps {
    presetRosterType?: RosterType
    mappingFilter?: CandidateFilterStateWithMetadata
    edit?: boolean
    refetch?: ApolloQueryRefetchFunction<
        RosterProfileQuery,
        RosterProfileQueryVariables
    >
    setIsLoading: (boolean) => void
}

interface FormValues {
    rosterName: string
    rosterType: RosterType | null
    company: GetCompaniesByNameQuery['companiesByName']['data'][number] | null
    clientContacts: GetCompanySpecificOptionsQuery['company']['employees']
    rosterStatus: (typeof rosterStatusOptions)[number]
    processStatus:
        | GetAddEditRosterOptionsQuery['processStatuses'][number]
        | null
    titles: GetAddEditRosterOptionsQuery['titles']
    positionCount: { [key: string]: number } | {}
    industry: GetAddEditRosterOptionsQuery['industries']
    sector: GetAddEditRosterOptionsQuery['sectors']
    location: GetCompanySpecificOptionsQuery['company']['companyOffices']
    startDates: GetAddEditRosterOptionsQuery['rosterStartDates']
    consultants: boolean
    visaStatuses: { id: string; value: string }[]
    internalNote: string
    scratchPad: string
}

const validationSchema = yup.object({
    rosterName: yup.string().required('Roster Name is required'),
    company: yup.object<FormValues['company'] | null>().nullable(),
    clientContacts: yup.array<FormValues['clientContacts']>().of(yup.object()),
    rosterStatus: yup.object<FormValues['rosterStatus']>().shape({
        id: yup
            .mixed<RosterStatus>()
            .oneOf(Object.values(RosterStatus), 'Invalid roster status'),
        label: yup.string(),
    }),
    processStatus: yup.object<FormValues['processStatus']>(),
    titles: yup.array<FormValues['titles']>().of(yup.object()),
    positionCount: yup.object<FormValues['positionCount']>(),
    industry: yup.array<FormValues['industry']>().of(yup.object()),
    sector: yup.array<FormValues['sector']>().of(yup.object()),
    location: yup.array<FormValues['location']>().of(yup.object()),
    startDates: yup.array().of(yup.string()),
    rosterType: yup
        .mixed<RosterType>()
        .oneOf(Object.values(RosterType), 'Invalid roster type')
        .required('Roster Type is required'),
})

const AddOrEdit: FC<Props> = ({
    selectedCandidates,
    edit,
    onClose,
    rosterId,
    refetch,
    setIsLoading,
    presetRosterType,
    mappingFilter,
}) => {
    const theme = useTheme()
    const toast = useToast()
    const navigate = useNavigate()

    const [updateRoster, updateRosterResult] = useMutation(
        EditRoster as DocumentNode
    )
    const [createRoster, createRosterResult] = useMutation(
        CreateRoster as DocumentNode
    )
    const isLoading = createRosterResult?.loading || updateRosterResult?.loading

    useEffect(() => {
        setIsLoading(isLoading)
    }, [isLoading, setIsLoading])

    const handleError = (e: ApolloError) => {
        toast({ title: 'Error', message: e.message })
    }

    const handleSubmit = (values: FormValues) => {
        console.log(
            '[E2E_DEBUG] handleSubmit called, rosterName:',
            values.rosterName,
            'rosterType:',
            values.rosterType
        )
        const sharedVars = {
            rosterName: values.rosterName,
            rosterType: values.rosterType,
            companyId: values.company?.id || null,
            industryIds: values.industry.map((industry) => industry.id),
            sectorIds: values.sector.map((sector) => sector.id),
            companyOfficeIds:
                values.location?.map((location) => location?.id || '') || [],
            rosterStatus: values.rosterStatus.id,
            positions: values.titles.map((title) => ({
                titleId: title.id,
                placementCount: values.positionCount[title.id] || 1,
            })),
            clientContactIds:
                values.clientContacts?.map((contact) => contact.id) || [],
            startDates: values.startDates,
            consultants: values.consultants,
            visaStatusIds: values.visaStatuses.map((vs) => vs.id),
            scratchPad: values.scratchPad,
        }
        if (edit) {
            updateRoster({
                variables: {
                    ...sharedVars,
                    rosterId: rosterId || '',
                },
                onCompleted: (data) => {
                    if (data.updateRoster.success) {
                        onClose()
                        if (refetch) refetch()
                        toast({
                            title: 'Success',
                            message: 'Search has been updated.',
                            severity: 'success',
                        })
                    } else {
                        toast({
                            title: 'Error',
                            message:
                                data.updateRoster.message ||
                                'Issue editing the roster, please try again.',
                        })
                    }
                },
                onError: handleError,
            })
        } else {
            console.log(
                '[E2E_DEBUG] calling createRoster, candidateIds:',
                selectedCandidates.length
            )
            createRoster({
                variables: {
                    ...sharedVars,
                    rosterType: values.rosterType ?? RosterType.Search,
                    initialCandidateProcessStatusId:
                        values.processStatus?.id || '',
                    candidateIds: selectedCandidates,
                    mappingFilter,
                    internalNote: values.internalNote || undefined,
                },
                onCompleted: (data) => {
                    const searchPath = `${recruiterRouteConfig.rosters.path}/${data.addRoster.id}`
                    if (selectedCandidates.length > 0) {
                        onClose()
                        toast({
                            title: 'Success',
                            message: 'Search has been created.',
                            severity: 'success',
                            action: {
                                title: 'View Search',
                                onClick: () => navigate(searchPath),
                            },
                        })
                    } else {
                        navigate(searchPath)
                        toast({
                            title: 'Success',
                            message: 'Search has been created.',
                            severity: 'success',
                        })
                    }
                },
                onError: (e) => {
                    console.log('[E2E_DEBUG] createRoster error:', e.message)
                    handleError(e)
                },
            })
        }
    }

    const formik = useFormik<FormValues>({
        initialValues: {
            rosterName: '',
            company: null,
            clientContacts: [],
            rosterStatus: { id: RosterStatus.Active, label: 'Active' },
            processStatus: null,
            titles: [],
            positionCount: {},
            industry: [],
            sector: [],
            location: [],
            startDates: [],
            consultants: false,
            visaStatuses: [],
            rosterType: presetRosterType ?? null,
            internalNote: '',
            scratchPad: '',
        },
        validationSchema,
        onSubmit: handleSubmit,
    })

    const { data: options, loading: optionsLoading } = useQuery(
        GetAddEditRosterOptions,
        {
            onCompleted: (data) => {
                const noStatus = data.processStatuses.find(
                    (status) => status.status === ProcessStatus.NoStatus
                )
                formik.setFieldValue('processStatus', noStatus)
            },
        }
    )
    const { data: companySpecificOptions } = useQuery(
        GetCompanySpecificOptions,
        {
            variables: { companyId: formik.values.company?.id || '' },
            skip: !formik.values.company?.id,
        }
    )
    useQuery(GetRosterData, {
        variables: { rosterId: rosterId! },
        skip: !rosterId,
        onCompleted: (data) => {
            formik.setFieldValue('rosterName', data.roster?.rosterName || '')
            formik.setFieldValue('rosterType', data.roster?.rosterType || null)
            formik.setFieldValue('company', data.roster?.company || null)
            formik.setFieldValue(
                'clientContacts',
                data.roster?.clientContacts?.map((cc) => cc.candidate) || []
            )
            formik.setFieldValue(
                'rosterStatus',
                rosterStatusOptions.find(
                    (option) => option.id === data.roster?.status
                ) || { id: RosterStatus.Active, label: 'Active' }
            )
            formik.setFieldValue(
                'industry',
                data.roster?.industries?.map((industry) => industry.industry) ||
                    []
            )
            formik.setFieldValue(
                'titles',
                data.roster?.placements?.map((p) => p.title) || []
            )
            formik.setFieldValue(
                'positionCount',
                data.roster?.placements?.reduce(
                    (acc, val) =>
                        val?.title?.id
                            ? {
                                  ...acc,
                                  [val?.title?.id]: val.placementCount || 1,
                              }
                            : acc,
                    {}
                )
            )
            formik.setFieldValue(
                'sector',
                data.roster?.sectors?.map((sector) => sector.sector) || []
            )
            formik.setFieldValue(
                'location',
                data.roster?.companyOffices?.map((co) => co?.companyOffice) ||
                    []
            )
            formik.setFieldValue(
                'startDates',
                data.roster?.startDates?.map((sd) => sd.startDateLabel) || []
            )
            formik.setFieldValue(
                'consultants',
                data.roster?.consultants ?? false
            )
            formik.setFieldValue(
                'visaStatuses',
                data.roster?.visaStatuses
                    ?.map((vs) => vs.visaStatus)
                    .filter(Boolean) || []
            )
            formik.setFieldValue('scratchPad', data.roster?.scratchPad ?? '')
        },
    })

    return (
        <Stack
            direction="column"
            justifyContent="space-between"
            sx={{
                flexGrow: 1,
            }}
        >
            <Stack component="form" sx={{ gap: getPxAsRem(24) }}>
                {/* ROSTER Type */}
                {!presetRosterType && (
                    <Autocomplete
                        options={Object.values(RosterType)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Type"
                                helperText={
                                    formik.touched.rosterType &&
                                    formik.errors.rosterType
                                }
                                error={
                                    formik.touched.rosterType &&
                                    Boolean(formik.errors.rosterType)
                                }
                                onBlur={formik.handleBlur}
                                name="rosterType"
                            />
                        )}
                        onChange={(e, value) =>
                            formik.setFieldValue('rosterType', value)
                        }
                        value={formik.values.rosterType}
                        disabled={edit || !!presetRosterType || isLoading}
                    />
                )}

                {/* ROSTER NAME */}
                <TextField
                    label="Name"
                    fullWidth
                    id="rosterName"
                    name="rosterName"
                    value={formik.values.rosterName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                        formik.touched.rosterName &&
                        Boolean(formik.errors.rosterName)
                    }
                    helperText={
                        formik.touched.rosterName && formik.errors.rosterName
                    }
                    disabled={isLoading}
                />

                {/* COMPANY */}
                <CompaniesAutocomplete
                    id="company"
                    getOptionLabel={(option) => option.value}
                    value={formik.values.company}
                    onChange={(e, value) => {
                        formik.setFieldValue('company', value)
                        formik.setFieldValue('clientContacts', [])
                        formik.setFieldValue('location', [])
                    }}
                    onBlur={formik.handleBlur}
                    disabled={isLoading}
                />
                {formik.values.rosterType === RosterType.Search && (
                    <>
                        {/* CLIENT CONTACTS */}
                        <Autocomplete
                            disabled={isLoading}
                            multiple
                            id="clientContacts"
                            options={
                                companySpecificOptions?.company.employees || []
                            }
                            getOptionLabel={(option) =>
                                option?.user ? getFullName(option.user) : '-'
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Client Contacts"
                                    error={
                                        formik.touched.clientContacts &&
                                        Boolean(formik.errors.clientContacts)
                                    }
                                    helperText={
                                        formik.touched.clientContacts &&
                                        formik.errors.clientContacts
                                    }
                                />
                            )}
                            value={formik.values.clientContacts || []}
                            onChange={(e, value) =>
                                formik.setFieldValue('clientContacts', value)
                            }
                            onBlur={formik.handleBlur}
                        />

                        {/* ROSTER STATUS */}
                        <Autocomplete
                            disabled={isLoading}
                            id="rosterStatus"
                            options={rosterStatusOptions}
                            isOptionEqualToValue={(option, value) =>
                                option.id === value.id
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Search Status"
                                    error={
                                        formik.touched.rosterStatus &&
                                        Boolean(formik.errors.rosterStatus)
                                    }
                                    helperText={
                                        formik.touched.rosterStatus && (
                                            <>{formik.errors.rosterStatus}</>
                                        )
                                    }
                                />
                            )}
                            getOptionLabel={(option) => option.label}
                            value={formik.values.rosterStatus}
                            onChange={(e, value) =>
                                formik.setFieldValue('rosterStatus', value)
                            }
                            onBlur={formik.handleBlur}
                        />
                        {/* CONSULTANTS */}
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formik.values.consultants}
                                    onChange={(e) =>
                                        formik.setFieldValue(
                                            'consultants',
                                            e.target.checked
                                        )
                                    }
                                    disabled={isLoading}
                                />
                            }
                            label="Consultants"
                        />

                        {/* VISA STATUSES */}
                        <Autocomplete
                            disabled={isLoading}
                            multiple
                            id="visaStatuses"
                            isOptionEqualToValue={(option, value) =>
                                option.id === value.id
                            }
                            options={options?.visaStatuses || []}
                            getOptionLabel={(option) => option.value}
                            renderInput={(params) => (
                                <TextField {...params} label="Visa" />
                            )}
                            value={formik.values.visaStatuses}
                            onChange={(_, value) =>
                                formik.setFieldValue('visaStatuses', value)
                            }
                        />

                        {/* CANDIDATE PROCESS STATUS */}
                        {!edit && (
                            <Autocomplete
                                disabled={isLoading}
                                id="processStatus"
                                options={options?.processStatuses || []}
                                groupBy={(option) =>
                                    option.processStatusSummaryGroup?.label ||
                                    ''
                                }
                                value={formik.values.processStatus}
                                getOptionLabel={(option) => option.statusLabel}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Candidate Process Status"
                                        error={
                                            formik.touched.processStatus &&
                                            Boolean(formik.errors.processStatus)
                                        }
                                        helperText={
                                            formik.touched.processStatus &&
                                            formik.errors.processStatus
                                        }
                                    />
                                )}
                                onChange={(e, value) =>
                                    formik.setFieldValue('processStatus', value)
                                }
                                onBlur={formik.handleBlur}
                            />
                        )}

                        <TextField
                            label="Scratch Pad"
                            fullWidth
                            multiline
                            minRows={3}
                            id="scratchPad"
                            name="scratchPad"
                            value={formik.values.scratchPad}
                            onChange={formik.handleChange}
                            disabled={isLoading}
                        />

                        {!edit && (
                            <TextField
                                label="Internal Note"
                                fullWidth
                                multiline
                                minRows={3}
                                id="internalNote"
                                name="internalNote"
                                value={formik.values.internalNote}
                                onChange={formik.handleChange}
                                disabled={isLoading}
                                placeholder="Note will be prepended to existing internal notes"
                            />
                        )}
                        <Divider />

                        <Typography variant="h5">Openings</Typography>

                        {/* POSITIONS */}
                        <Typography id="positionsLabel" variant="subtitle2">
                            Select all the different positions to be placed on
                            this roster.
                        </Typography>
                        <Autocomplete
                            multiple
                            disabled={isLoading}
                            id="titles"
                            options={options?.titles || []}
                            getOptionLabel={(option) => option.value}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label=""
                                    hiddenLabel
                                    aria-labelledby="positionsLabel"
                                    error={
                                        formik.touched.titles &&
                                        Boolean(formik.errors.titles)
                                    }
                                    helperText={
                                        formik.touched.titles && (
                                            <>{formik.errors.titles}</>
                                        )
                                    }
                                    onBlur={formik.handleBlur}
                                    name="titles"
                                />
                            )}
                            value={formik.values.titles || []}
                            onChange={(e, value) =>
                                formik.setFieldValue('titles', value)
                            }
                            onBlur={formik.handleBlur}
                        />
                        {formik.values.titles.length > 0 && (
                            <>
                                {/*  POSITION COUNTS */}
                                <Typography
                                    id="positionsCountLabel"
                                    variant="subtitle2"
                                >
                                    Add the number of candidates that need to be
                                    placed in each position.
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: getPxAsRem(24),
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    {formik.values.titles?.map((position) => (
                                        <TextField
                                            disabled={isLoading}
                                            key={position.id}
                                            label={position.value}
                                            InputProps={{
                                                inputProps: { min: 1 },
                                            }}
                                            type="number"
                                            sx={{
                                                maxWidth: getPxAsRem(140),
                                            }}
                                            value={
                                                formik.values.positionCount[
                                                    position.id
                                                ]?.toString() || 1
                                            }
                                            onChange={(e) =>
                                                formik.setFieldValue(
                                                    'positionCount',
                                                    {
                                                        ...formik.values
                                                            .positionCount,
                                                        [position.id]:
                                                            +e.target.value,
                                                    }
                                                )
                                            }
                                        />
                                    ))}
                                </Box>
                                {/* sum of each of the above inputs */}
                                <Typography variant="h6">
                                    {`Total Openings = `}
                                    <Box
                                        sx={{
                                            display: 'inline',
                                            color: theme.palette.primary.main,
                                        }}
                                    >
                                        {formik.values.titles?.reduce(
                                            (acc, val) => {
                                                const countAtPosition =
                                                    formik.values.positionCount[
                                                        val.id
                                                    ] || 1
                                                return acc + countAtPosition
                                            },
                                            0
                                        )}
                                    </Box>
                                </Typography>
                            </>
                        )}

                        <Divider />

                        {/* INDUSTRIES */}
                        <Autocomplete
                            disabled={isLoading}
                            multiple
                            isOptionEqualToValue={(option, value) =>
                                option.id === value.id
                            }
                            options={options?.industries || []}
                            getOptionLabel={(option) => option.value}
                            renderInput={(params) => (
                                <>
                                    <Typography
                                        variant="h5"
                                        id="industry-label"
                                        sx={{ marginBottom: getPxAsRem(8) }}
                                    >
                                        Industry
                                    </Typography>
                                    <TextField
                                        {...params}
                                        aria-labelledby="industry-label"
                                        // https://github.com/jaredpalmer/formik/issues/3683
                                        helperText={
                                            formik.touched.industry && (
                                                <>{formik.errors.industry}</>
                                            )
                                        }
                                        error={
                                            formik.touched.industry &&
                                            Boolean(formik.errors.industry)
                                        }
                                        onBlur={formik.handleBlur}
                                        name="industry"
                                    />
                                </>
                            )}
                            onChange={(e, value) =>
                                formik.setFieldValue('industry', value)
                            }
                            value={formik.values.industry}
                        />
                        <Divider />

                        {/*  SECTORS */}
                        <Autocomplete
                            disabled={isLoading}
                            multiple
                            isOptionEqualToValue={(option, value) =>
                                option.id === value.id
                            }
                            options={options?.sectors || []}
                            getOptionLabel={(option) => option.value}
                            renderInput={(params) => (
                                <>
                                    <Typography
                                        variant="h5"
                                        id="sector-label"
                                        sx={{ marginBottom: getPxAsRem(8) }}
                                    >
                                        Sector
                                    </Typography>
                                    <TextField
                                        {...params}
                                        aria-labelledby="sector-label"
                                        name="sector"
                                        helperText={
                                            formik.touched.sector && (
                                                <>{formik.errors.sector}</>
                                            )
                                        }
                                        error={
                                            formik.touched.sector &&
                                            Boolean(formik.errors.sector)
                                        }
                                        onBlur={formik.handleBlur}
                                    />
                                </>
                            )}
                            onChange={(e, value) =>
                                formik.setFieldValue('sector', value)
                            }
                            value={formik.values.sector}
                        />
                        <Divider />

                        {/* LOCATIONS */}
                        <Autocomplete
                            disabled={isLoading}
                            isOptionEqualToValue={(option, value) =>
                                option?.id === value?.id
                            }
                            multiple
                            options={
                                companySpecificOptions?.company
                                    .companyOffices || []
                            }
                            getOptionLabel={(option) =>
                                option.officeLocation?.value || ''
                            }
                            renderInput={(params) => (
                                <>
                                    <Typography
                                        variant="h5"
                                        id="location-label"
                                        sx={{ marginBottom: getPxAsRem(8) }}
                                    >
                                        Location
                                    </Typography>
                                    <TextField
                                        {...params}
                                        aria-labelledby="location-label"
                                        helperText={
                                            formik.touched.location && (
                                                <>{formik.errors.location}</>
                                            )
                                        }
                                        error={
                                            formik.touched.location &&
                                            Boolean(formik.errors.location)
                                        }
                                        onBlur={formik.handleBlur}
                                        name="location"
                                    />
                                </>
                            )}
                            onChange={(e, value) =>
                                formik.setFieldValue('location', value)
                            }
                            value={formik.values.location || []}
                        />
                        <Divider />

                        {/* START DATE*/}
                        <Autocomplete
                            multiple
                            disabled={isLoading}
                            options={options?.rosterStartDates || []}
                            renderInput={(params) => (
                                <>
                                    <Typography
                                        variant="h5"
                                        id="startDates-label"
                                        sx={{ marginBottom: getPxAsRem(8) }}
                                    >
                                        Start Date
                                    </Typography>
                                    <TextField
                                        {...params}
                                        aria-labelledby="startDates-label"
                                        helperText={
                                            formik.touched.startDates &&
                                            formik.errors.startDates
                                        }
                                        error={
                                            formik.touched.startDates &&
                                            Boolean(formik.errors.startDates)
                                        }
                                        onBlur={formik.handleBlur}
                                        name="startDates"
                                    />
                                </>
                            )}
                            onChange={(e, value) =>
                                formik.setFieldValue('startDates', value)
                            }
                            value={formik.values.startDates}
                        />
                        <Divider />
                    </>
                )}
            </Stack>
            <DrawerCancelSave
                isEditMode={false}
                saveButtonText={
                    edit || selectedCandidates.length === 0
                        ? 'Save'
                        : `Save (${selectedCandidates.length})`
                }
                isCancelDisabled={isLoading}
                isLoading={isLoading}
                isSaveDisabled={isLoading || optionsLoading}
                handleCancel={onClose}
                handleSave={() => {
                    console.log(
                        '[E2E_DEBUG] Save clicked, isValid:',
                        formik.isValid,
                        'errors:',
                        JSON.stringify(formik.errors)
                    )
                    formik.handleSubmit()
                }}
            />
        </Stack>
    )
}

export default AddOrEdit
